import type { DocTopic } from "./registry";
import { compareResults, matchLabel, normalize } from "@/lib/search/match";
import { DOC_CONTENT_INDEX } from "./content-index";

export interface DocTopicMatch {
  topic: DocTopic;
  matches: [number, number][];
  score: number;
}

/**
 * Search the documentation index like a small knowledge base. Exact phrase
 * matches keep the global-search ranking; multi-word questions can also match
 * when every meaningful word appears somewhere in the topic's indexed text.
 */
export function searchDocumentation(
  query: string,
  topics: readonly DocTopic[],
): DocTopicMatch[] {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const tokens = normalize(trimmed)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 1);

  return topics
    .flatMap((topic) => {
      const synonyms = [
        topic.category,
        topic.slug.replaceAll("-", " "),
        ...(topic.keywords ?? []),
        DOC_CONTENT_INDEX[topic.slug] ?? "",
      ];
      const phraseHit = matchLabel(topic.title, trimmed, synonyms);

      if (phraseHit) {
        return [{ topic, matches: phraseHit.matches, score: phraseHit.score }];
      }

      if (tokens.length === 0) return [];
      const searchable = normalize([topic.title, ...synonyms].join(" "));
      if (!tokens.every((token) => searchable.includes(token))) return [];

      // Token-complete matches sit below any phrase/synonym match, but reward
      // more specific questions so they sort consistently among themselves.
      return [{ topic, matches: [], score: 20 + tokens.length }];
    })
    .sort((a, b) => compareResults(
      { score: a.score, title: a.topic.title },
      { score: b.score, title: b.topic.title },
    ));
}
